import {
  getCubaREST,
  getPropertyInfo,
  isDateProperty,
  isDateTimeProperty,
  isTimeProperty,
  isToManyRelation,
  isToOneRelation,
  MainStore
} from "@cuba-platform/react";
import Notification from "./Notification/Notification";
import {AbstractDictionary} from "../../cuba/entities/base/AbstractDictionary";
import {AbstractBprocRequest} from "../../cuba/entities/base/AbstractBprocRequest";
import {AssignedPerformancePlan} from "../../cuba/entities/base/tsadv$AssignedPerformancePlan";
import {PersonExt} from "../../cuba/entities/base/base$PersonExt";
import {History} from "history";
import {toJS} from "mobx";
import moment__default from "moment";
import {SerializedEntity} from "@cuba-platform/rest";
import {UcoSignSignature} from "../../cuba/entities/base/ucosign$Signature";

export const getBlobUrl = (fileId: string): Promise<string> => {
  return getCubaREST()!.getFile(fileId).then(responseBlob => URL.createObjectURL(responseBlob));
};

export const getFileUrl = (fileId: string): string => {
  return `${getCubaREST()!.apiUrl}v2/files/${fileId}?access_token=${getCubaREST()!.restApiToken}`;
};

export const getFileUrlOpt = (fileId?: string|null): string|undefined => {
  if (!fileId) return undefined;
  return `${getCubaREST()!.apiUrl}v2/files/${fileId}?access_token=${getCubaREST()!.restApiToken}`;
};

export const parseUrlForUserCode = (data: UcoSignSignature) => {
  if (!data.dataURL) return ''
  const parts = data.dataURL.split('/')
  return parts[parts.length - 1]
}
export const downloadFile = (fileId: string, fileName: string, extension: string, fileNotFoundMessage: string) => {
  return getCubaREST()!.getFile(fileId)
    .then(value => saveFile(value, `${fileName}.${extension}`))
    .catch(reason => {
      Notification.error({
        message: fileNotFoundMessage,
      });
      throw reason;
    });
};

export const saveFile = (value: Blob, fileNameWithExtension: string) => {
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(value);
  anchor.target = '_blank';
  anchor.download = fileNameWithExtension;

  anchor.click();
};

export const openPdfInNewTab = (fileId: string, fileName: string, fileNotFoundMessage: string) => {
  return getCubaREST()!.getFile(fileId)
    .then((value: Blob) => {
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(value);
      anchor.target = '_blank';
      anchor.name = fileName;

      anchor.click();
    }).catch(reason => {
      Notification.error({
        message: fileNotFoundMessage,
      });

      throw reason;
    });
};

export const link = (entityName: string) => {
  const link = entityName!.substring(Math.max(entityName!.indexOf("$"), entityName!.indexOf("_")) + 1);
  return "/" + link.charAt(0).toLocaleLowerCase() + link.substring(1);
};

export const wrapSrcBase64 = (url: string) => {
  return "data:image/png;base64, " + url;
};

export const dicValue = (dictionary?: AbstractDictionary, lang?: string) => {
  return dictionary && lang && dictionary[langValue(lang)];
};

export const langValue = (lang: string) => {
  lang = lang.toLocaleLowerCase();
  switch (lang) {
    case "ru":
      return "langValue1";
    case "en":
      return "langValue3";
    case "kz":
      return "langValue2";
  }
  return "langValue1";
};

export const getLocaleName = (localName: string) => {
  switch (localName) {
    case "en" : {
      return "En";
    }
    case "kz" : {
      return "Kz";
    }
    default: {
      return "";
    }
  }
};

export const getBusinessKey = (request: AbstractBprocRequest) => {
  if (request instanceof AssignedPerformancePlan || request["_entityName"] === AssignedPerformancePlan.NAME)
    return request.id + "/" + (request["stage"] && request["stage"]["code"]);
  return request.id;
};

export const getFullName = (person: PersonExt, lang: string): string => {
  lang = lang.toLocaleLowerCase();
  if (lang === 'en') return person.lastNameLatin + ' ' + person.middleNameLatin + ' ' + person.firstNameLatin;
  return person.lastName + ' ' + person.middleName + ' ' + person.firstName;
};

export const catchException = (promise: Promise<any>): Promise<any> => {
  return promise.catch(async (response: any) => {
    const reader = response.response.body.getReader();

    let receivedLength = 0;
    let chunks = [];
    while (true) {
      const {done, value} = await reader.read();

      if (done) {
        break;
      }

      chunks.push(value);
      receivedLength += value.length;
    }

    let chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (let chunk of chunks) {
      chunksAll.set(chunk, position);
      position += chunk.length;
    }

    let result = new TextDecoder("utf-8").decode(chunksAll);
    const parse = JSON.parse(result);
    throw new Error(parse.message);
  })
};

export const isNumber = (number: any): boolean => {
  return number !== undefined && number !== null && !isNaN(number);
}

export const isEquals = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === undefined) return false;
  if (b === undefined) return false;
  return (a['id'] || b['id']) !== undefined && a['id'] === b['id'];
}

export const goBackOrHomePage = (history: History) => {
  if (history.action === 'POP')
    history.push("/");
  else history.goBack();
}

export const capitalizeFirstLetter = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function setItemPropertiesValue<T extends SerializedEntity<any>>(mainStore: MainStore, entityName: string, item: T, entityPatch: any) {
  const metadata = toJS(mainStore.metadata)!;
  const normalizedPatch = Object.assign({}, entityPatch);
  Object.entries(entityPatch).forEach(([key, value]) => {
    const propInfo = getPropertyInfo(metadata, entityName, key);
    if (propInfo && isToOneRelation(propInfo)
      && typeof value === 'string') {
      normalizedPatch[key] = {id: value};
    }
    if (propInfo && isToManyRelation(propInfo) && Array.isArray(value)) {
      normalizedPatch[key] = value.map(id => ({id}));
    }
    if (propInfo && isDateProperty(propInfo) && moment__default.isMoment(value)) {
      normalizedPatch[key] = value.format('YYYY-MM-DD');
    }
    if (propInfo && isTimeProperty(propInfo) && moment__default.isMoment(value)) {
      normalizedPatch[key] = value.format('HH:mm:ss');
    }
    if (propInfo && isDateTimeProperty(propInfo) && moment__default.isMoment(value)) {
      normalizedPatch[key] = value.format('YYYY-MM-DD HH:mm:ss.SSS');
    }
    if (value === '' || value == null) {
      normalizedPatch[key] = null;
    }
  });
  return Object.assign(item, normalizedPatch);
}